import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { Send, Wifi, WifiOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

export default function ChatPanel({ ticketId }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [connected, setConnected] = useState(false);
  const [typing, setTyping] = useState(null);
  const bottomRef = useRef(null);
  const wsRef = useRef(null);
  const reconnectRef = useRef(null);
  const pollRef = useRef(null);
  const wsFailureCount = useRef(0);
  const wsGivenUp = useRef(false);

  // Determine WebSocket URL from backend URL; short-lived ticket fetched per connect
  const getWsUrl = useCallback(async () => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    let token = '';
    try {
      const { data } = await api.get('/api/auth/ws-ticket');
      token = data?.token || '';
    } catch (err) {
      console.warn('[chat] ws-ticket fetch failed', err);
    }
    const wsBase = backendUrl.replace('https://', 'wss://').replace('http://', 'ws://');
    return `${wsBase}/api/ws/chat/${ticketId}?token=${token}`;
  }, [ticketId]);

  // Fallback: HTTP polling
  const fetchMessages = useCallback(async () => {
    if (!ticketId) return;
    try {
      const { data } = await api.get(`/api/chat/messages/${ticketId}`);
      setMessages(data.messages || []);
    } catch (e) { /* handled */ }
  }, [ticketId]);

  // Connect WebSocket with exponential backoff + circuit breaker
  const connectWs = useCallback(async () => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;
    if (wsGivenUp.current) return;

    try {
      const wsUrl = await getWsUrl();
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        setConnected(true);
        wsFailureCount.current = 0;
        // Stop polling when WS is connected
        if (pollRef.current) clearInterval(pollRef.current);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'history') {
            setMessages(data.messages || []);
          } else if (data.type === 'new_message') {
            setMessages(prev => {
              if (prev.some(m => m.id === data.message.id)) return prev;
              return [...prev, data.message];
            });
          } else if (data.type === 'typing') {
            if (data.user_id !== user?.id) {
              setTyping(data.user_name);
              setTimeout(() => setTyping(null), 2000);
            }
          }
        } catch (e) { /* handled */ }
      };

      ws.onclose = () => {
        setConnected(false);
        wsFailureCount.current += 1;
        // Always start polling fallback so messages still flow
        if (!pollRef.current) pollRef.current = setInterval(fetchMessages, 5000);
        // Stop retrying WS after 6 failures — polling takes over permanently
        if (wsFailureCount.current >= 6) {
          wsGivenUp.current = true;
          return;
        }
        const delay = Math.min(3000 * Math.pow(2, wsFailureCount.current - 1), 5 * 60 * 1000);
        reconnectRef.current = setTimeout(() => connectWs(), delay);
      };

      ws.onerror = () => {
        ws.close();
      };
    } catch {
      // WebSocket not supported or connection failed - use polling
      setConnected(false);
      pollRef.current = setInterval(fetchMessages, 5000);
    }
  }, [getWsUrl, fetchMessages, user?.id]);

  useEffect(() => {
    fetchMessages(); // Initial load
    connectWs();

    return () => {
      if (wsRef.current) wsRef.current.close();
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [ticketId, connectWs, fetchMessages]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Keep-alive ping
  useEffect(() => {
    const ping = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30000);
    return () => clearInterval(ping);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSend = async (e) => {
    e.preventDefault();
    if (!input.trim() || sending) return;
    const content = input.trim();
    setInput('');
    setSending(true);

    // Try WebSocket first
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'message', content }));
      setSending(false);
    } else {
      // Fallback to HTTP
      try {
        await api.post('/api/chat/messages', {
          ticket_id: ticketId, content, type: 'text',
        });
        await fetchMessages();
      } catch (e) { /* handled */ } finally {
        setSending(false);
      }
    }
  };

  const handleTyping = () => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'typing' }));
    }
  };

  return (
    <div className="flex flex-col h-full border border-gray-200 rounded-lg bg-white" data-testid="chat-panel">
      <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900">Messages</h3>
        <span className={`flex items-center gap-1 text-[10px] font-medium ${connected ? 'text-emerald-500' : 'text-gray-400'}`} data-testid="ws-status">
          {connected ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
          {connected ? 'Live' : 'Polling'}
        </span>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-[200px] max-h-[400px]">
        {messages.length === 0 && (
          <p className="text-sm text-gray-400 text-center py-8">No messages yet. Start the conversation.</p>
        )}
        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`flex flex-col ${msg.sender_id === user?.id ? 'items-end' : 'items-start'}`}
            data-testid={`chat-message-${msg.id}`}
          >
            <div className={`max-w-[80%] rounded-lg px-3 py-2 ${
              msg.type === 'system'
                ? 'bg-gray-100 text-gray-500 text-xs italic w-full text-center'
                : msg.sender_id === user?.id
                  ? 'bg-[#D4A843] text-[#0A0A0A] font-semibold'
                  : 'bg-gray-100 text-gray-900'
            }`}>
              {msg.type !== 'system' && (
                <p className={`text-[10px] font-medium mb-0.5 ${msg.sender_id === user?.id ? 'text-blue-200' : 'text-gray-500'}`}>
                  {msg.sender_name}
                </p>
              )}
              <p className="text-sm">{msg.content}</p>
            </div>
            <span className="text-[10px] text-gray-400 mt-0.5 px-1">
              {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
        ))}
        {typing && (
          <div className="flex items-center gap-2 text-xs text-gray-400 animate-pulse">
            <span className="w-2 h-2 rounded-full bg-gray-300" />
            {typing} is typing...
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      <form onSubmit={handleSend} className="p-3 border-t border-gray-200 flex gap-2">
        <Input
          value={input}
          onChange={(e) => { setInput(e.target.value); handleTyping(); }}
          placeholder="Type a message..."
          className="flex-1 rounded-lg text-sm"
          data-testid="chat-input"
        />
        <Button
          type="submit"
          disabled={sending || !input.trim()}
          className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded-lg px-3"
          data-testid="chat-send-btn"
        >
          <Send className="w-4 h-4" />
        </Button>
      </form>
    </div>
  );
}
